import type { DatePickerProps } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import { buildTransportAddressFields } from '../modules/orders/transportAddressUtils';

export const TRANSPORT_DATE_FORMAT = 'YYYY-MM-DD';

const EMPTY_DATE_VALUE = null;

export interface TransportFormValues {
  customer?: string;
  currency?: string;
  status?: string;
  loadingAddresses?: string[];
  deliveryAddresses?: string[];
  loadingDate?: Dayjs | null;
  estimatedDelivery?: Dayjs | null;
  actualDelivery?: Dayjs | null;
  transportRate?: number | null;
  totalWeight?: number | null;
  totalVolume?: number | null;
  packages?: number | null;
  [key: string]: unknown;
}

export const transportDatePickerProps: DatePickerProps = {
  format: TRANSPORT_DATE_FORMAT,
  needConfirm: false,
  style: { width: '100%' },
};

export function buildTransportFormValues(
  transport: Record<string, any> | undefined,
  order: Record<string, any> | undefined,
): TransportFormValues {
  if (transport) {
    const addressFields = buildTransportAddressFields(transport);

    return {
      ...transport,
      ...addressFields,
      loadingDate: toDayjsValue(transport.loadingDate),
      estimatedDelivery: toDayjsValue(transport.estimatedDelivery),
      actualDelivery: toDayjsValue(transport.actualDelivery),
      transportRate: toNumberValue(transport.transportRate),
      totalWeight: toNumberValue(transport.totalWeight),
      totalVolume: toNumberValue(transport.totalVolume),
      packages: transport.packages ?? null,
    };
  }

  const addressFields = buildTransportAddressFields({
    loadingAddresses: order?.factoryLocation ? [order.factoryLocation] : [],
    deliveryAddresses: order?.deliveryLocation ? [order.deliveryLocation] : [],
  });
  const loadingAddresses = addressFields.loadingAddresses.length > 0 ? addressFields.loadingAddresses : [''];
  const deliveryAddresses = addressFields.deliveryAddresses.length > 0 ? addressFields.deliveryAddresses : [''];

  return {
    customer: order?.customer?.name || '',
    // PLN is the internal money base; default an unset order currency to PLN to
    // match the PLN-default rule rather than a legacy 'USD'.
    currency: order?.currency || 'PLN',
    status: 'PENDING',
    ...addressFields,
    loadingAddresses,
    deliveryAddresses,
    loadingDate: EMPTY_DATE_VALUE,
    estimatedDelivery: EMPTY_DATE_VALUE,
    actualDelivery: EMPTY_DATE_VALUE,
    transportRate: null,
    totalWeight: null,
    totalVolume: null,
    packages: null,
  };
}

export function buildTransportRequestPayload(values: TransportFormValues): Record<string, unknown> {
  const addressFields = buildTransportAddressFields(values as Record<string, unknown>);

  return {
    ...values,
    ...addressFields,
    loadingDate: toDateString(values.loadingDate),
    estimatedDelivery: toDateString(values.estimatedDelivery),
    actualDelivery: toDateString(values.actualDelivery),
    transportRate: toStringNumber(values.transportRate),
    totalWeight: toStringNumber(values.totalWeight),
    totalVolume: toStringNumber(values.totalVolume),
  };
}

function toDayjsValue(value: unknown): Dayjs | null {
  if (!value) {
    return EMPTY_DATE_VALUE;
  }

  const dateValue = dayjs(value as dayjs.ConfigType);
  return dateValue.isValid() ? dateValue : EMPTY_DATE_VALUE;
}

function toNumberValue(value: unknown): number | null {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  const parsedValue = Number(value);
  return Number.isFinite(parsedValue) ? parsedValue : null;
}

function toDateString(value: unknown): string | null {
  if (!dayjs.isDayjs(value)) {
    return null;
  }

  return value.format(TRANSPORT_DATE_FORMAT);
}

function toStringNumber(value: unknown): string | null {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  return String(value);
}
